// src/services/streakService.ts
// Win-streak persistence layer.
//
// Storage strategy (intentionally NOT routed through the progress retry
// queue — streak data is tiny, idempotent, and replaying a stale streak
// write would actively corrupt the value):
//   • IDB (STORE_META) is the local source of truth — read on mount.
//   • Firestore doc `streaks/{uid}` is the cross-device source of truth.
//   • On load: read both, keep whichever has the higher `lastUpdatedMs`.
//   • On bump: write IDB synchronously, fire-and-forget Firestore write.
//
// Day rollover semantics (LOCAL TIME, intentional):
//   • `lastActiveDay` is a YYYY-MM-DD string in the user's local TZ.
//   • If today's date matches `lastActiveDay`: no-op (already counted).
//   • If yesterday matches: increment streak (capped at 999).
//   • If older: reset to 1 (today becomes the new run).
//
// Display:
//   • `computeDisplayStreak()` returns 0 when the last active day is older
//     than yesterday — UI shows the broken streak immediately even though
//     stored value isn't reset until the next bump.

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { idbGet, idbSet, STORE_META, metaKey } from '../lib/idb-wrapper';

export const STREAK_CAP = 999;

export interface StreakRecord {
  currentStreak: number;
  /** YYYY-MM-DD in the user's local timezone. */
  lastActiveDay: string | null;
  /** Wall-clock ms epoch of the last bump. Used to resolve IDB vs Firestore conflicts. */
  lastUpdatedMs: number;
}

const EMPTY: StreakRecord = {
  currentStreak: 0,
  lastActiveDay: null,
  lastUpdatedMs: 0,
};

// ── Date helpers (local TZ) ──────────────────────────────────────────────────

/** YYYY-MM-DD in local time. Date.toISOString() is UTC — do NOT use it here. */
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYmd(): string {
  return ymd(new Date());
}

function yesterdayYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return ymd(d);
}

// ── IDB key ──────────────────────────────────────────────────────────────────

/** Per-user IDB key — anonymous users still get a streak under 'anon'. */
function streakIdbKey(uid: string | null): string {
  return metaKey(`streak:${uid || 'anon'}`);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Read the user's streak from IDB. Resolves Firestore's value into IDB
 * when the cloud copy is more recent (cross-device case).
 */
export async function loadStreak(uid: string | null): Promise<StreakRecord> {
  const local = (await idbGet<StreakRecord>(streakIdbKey(uid), STORE_META)) || EMPTY;

  // Anonymous users have no Firestore doc — return what we have.
  if (!uid) return local;

  let remote: StreakRecord | null = null;
  try {
    const snap = await getDoc(doc(db, 'streaks', uid));
    if (snap.exists()) {
      const data = snap.data();
      remote = {
        currentStreak: Math.max(0, Math.min(STREAK_CAP, Number(data.currentStreak) || 0)),
        lastActiveDay: typeof data.lastActiveDay === 'string' ? data.lastActiveDay : null,
        lastUpdatedMs: Number(data.lastUpdatedMs) || 0,
      };
    }
  } catch (err) {
    console.warn('[streakService] Firestore read failed, using local:', err);
  }

  // Pick the more recent record.
  const winner: StreakRecord =
    remote && remote.lastUpdatedMs > local.lastUpdatedMs ? remote : local;

  // If remote was newer, update IDB so subsequent reads are consistent.
  if (winner === remote) {
    try {
      await idbSet(streakIdbKey(uid), winner, STORE_META);
    } catch (err) {
      console.warn('[streakService] IDB writeback failed:', err);
    }
  }

  return winner;
}

/**
 * Record a completion (test or exercise section) for "today" and return the
 * updated record. Idempotent within the same calendar day.
 */
export async function bumpStreak(uid: string | null): Promise<StreakRecord> {
  const current = await loadStreak(uid);
  const today = todayYmd();
  const yest = yesterdayYmd();

  // Already counted today — no change.
  if (current.lastActiveDay === today && current.currentStreak > 0) {
    return current;
  }

  let nextStreak: number;
  if (current.lastActiveDay === yest) {
    nextStreak = Math.min(STREAK_CAP, current.currentStreak + 1);
  } else {
    // Either first ever, or a day was missed → fresh run starts at 1.
    nextStreak = 1;
  }

  const next: StreakRecord = {
    currentStreak: nextStreak,
    lastActiveDay: today,
    lastUpdatedMs: Date.now(),
  };

  // Write IDB first (offline-safe).
  try {
    await idbSet(streakIdbKey(uid), next, STORE_META);
  } catch (err) {
    console.error('[streakService] IDB write failed:', err);
  }

  // Fire-and-forget Firestore write. Failure is non-fatal — IDB still has it.
  if (uid) {
    void setDoc(doc(db, 'streaks', uid), next, { merge: true }).catch((err) => {
      console.warn('[streakService] Firestore write failed (will retry on next bump):', err);
    });
  }

  return next;
}

/**
 * UI-facing helper. Returns the streak count to DISPLAY based on whether the
 * last active day is still in the active window (today or yesterday).
 *
 * Stored value isn't mutated here — bumpStreak() will reset it the next time
 * the user actually completes something.
 */
export function computeDisplayStreak(record: StreakRecord | null | undefined): number {
  if (!record || record.currentStreak <= 0 || !record.lastActiveDay) return 0;
  const today = todayYmd();
  const yest = yesterdayYmd();
  if (record.lastActiveDay === today || record.lastActiveDay === yest) {
    return Math.min(record.currentStreak, STREAK_CAP);
  }
  return 0; // missed at least one full day
}

/** Format helper for graceful 999+ display. */
export function formatStreak(n: number): string {
  if (n >= STREAK_CAP) return `${STREAK_CAP}+`;
  return String(Math.max(0, Math.floor(n)));
}